/**
 * Modal for creating/editing machines
 */
import React, { useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Row,
  Col,
} from 'antd';
import dayjs from 'dayjs';
import type { Machine, MachineInput } from '../types';
import { DB_VENDORS, LICENSE_TYPES, DEFAULT_MACHINE_VALUES, PRODUCT_VERSIONS } from '../constants';

interface MachineFormModalProps {
  visible: boolean;
  machine: Machine | null;
  onCancel: () => void;
  onSubmit: (data: MachineInput) => Promise<void>;
  loading: boolean;
}

const MachineFormModal: React.FC<MachineFormModalProps> = ({
  visible,
  machine,
  onCancel,
  onSubmit,
  loading,
}) => {
  const [form] = Form.useForm<MachineInput>();
  const licenseType = Form.useWatch('license_type', form);

  useEffect(() => {
    if (visible) {
      if (machine) {
        // Edit mode - populate form with existing data
        form.setFieldsValue({
          ...machine,
          purchase_date: machine.purchase_date,
          supported_databases: machine.supported_databases,
        });
      } else {
        // Create mode - set defaults
        form.setFieldsValue({
          ...DEFAULT_MACHINE_VALUES,
          product_version: PRODUCT_VERSIONS[0],
          purchase_date: dayjs().format('YYYY-MM-DD'),
          supported_databases: 'oracle,mssql',
        });
      }
    }
  }, [visible, machine, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      // Convert supported_databases array to comma-separated string if needed
      const submitData: MachineInput = {
        ...values,
        supported_databases: Array.isArray(values.supported_databases)
          ? values.supported_databases.join(',')
          : values.supported_databases,
      };
      await onSubmit(submitData);
      form.resetFields();
    } catch (error) {
      // Validation failed
      console.error('Validation failed:', error);
    }
  };

  return (
    <Modal
      title={machine ? 'Edit Machine' : 'Add New Machine'}
      open={visible}
      onOk={handleOk}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      confirmLoading={loading}
      width={800}
      okText={machine ? 'Update' : 'Create'}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={DEFAULT_MACHINE_VALUES}
      >
        {machine ? (
          <>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="product_name"
                  label="Product Name"
                  rules={[{ required: true, message: 'Please enter product name' }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="product_version"
                  label="Product Version"
                  rules={[{ required: true, message: 'Please select product version' }]}
                >
                  <Select>
                    {PRODUCT_VERSIONS.map(v => (
                      <Select.Option key={v} value={v}>{v}</Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="product_license"
                  label="Product License"
                  rules={[{ required: true, message: 'Please enter product license' }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="license_type"
                  label="License Type"
                  rules={[{ required: true, message: 'Please select license type' }]}
                >
                  <Select>
                    {LICENSE_TYPES.map(lt => (
                      <Select.Option key={lt.value} value={lt.value}>{lt.label}</Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </>
        ) : (
          <>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="product_name"
                  label="Product Name"
                >
                  <Input disabled />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="license_type"
                  label="License Type"
                  rules={[{ required: true, message: 'Please select license type' }]}
                >
                  <Select>
                    {LICENSE_TYPES.map(lt => (
                      <Select.Option key={lt.value} value={lt.value}>{lt.label}</Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {licenseType === 'machine_code' && (
              <Form.Item
                name="machine_code"
                label="Machine Code"
                rules={[{ required: true, message: 'Please enter machine code' }]}
              >
                <Input.TextArea rows={3} placeholder="Enter machine code" />
              </Form.Item>
            )}

            {/* Hidden fields with defaults for create mode */}
            <Form.Item name="product_version" hidden><Input /></Form.Item>
            <Form.Item name="product_license" hidden><Input /></Form.Item>
          </>
        )}

        <Form.Item
          name="supported_databases"
          label="Supported Databases"
          rules={[{ required: true, message: 'Please select supported databases' }]}
        >
          <Select
            mode="multiple"
            placeholder="Select databases"
            maxTagCount={10}
          >
            {DB_VENDORS.map(db => (
              <Select.Option key={db.value} value={db.value}>{db.label}</Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="customer_company_name"
              label="Customer Company Name"
              rules={[{ required: true, message: 'Please enter company name' }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="customer_contact_name"
              label="Customer Contact Name"
              rules={[{ required: true, message: 'Please enter contact name' }]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="customer_contact_email"
          label="Customer Contact Email"
          rules={[
            { required: true, message: 'Please enter contact email' },
            { type: 'email', message: 'Please enter a valid email' },
          ]}
        >
          <Input />
        </Form.Item>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="purchase_date"
              label="Purchase Date"
              rules={[{ required: true, message: 'Please select purchase date' }]}
            >
              <Input type="date" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="available_days"
              label="Available Days"
              rules={[{ required: true, message: 'Please enter available days' }]}
            >
              <InputNumber min={1} max={9999} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="max_user"
              label="Max Users (Optional)"
            >
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        {machine && licenseType === 'machine_code' && (
          <Form.Item
            name="machine_code"
            label="Machine Code"
            rules={[{ required: true, message: 'Please enter machine code' }]}
          >
            <Input.TextArea rows={3} placeholder="Enter machine code" />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default MachineFormModal;
